"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import { ThemeToggle } from "./theme-toggle";
import { Menu02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { cn } from "@/utils/ui";
import { DEFAULT_LOGO_URL } from "@/site/brand";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { LanguageSelector } from "@/components/i18n/language-selector";
import { analyticsTrack } from "@/analytics/analytics";
import { getSiteTools } from "@/site/pages";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export function Header() {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const menuTriggerRef = useRef<HTMLButtonElement>(null);
	const closeMenu = () => setIsMenuOpen(false);
	const t = useTranslations("Navigation");
	const locale = useLocale();
	const tools = getSiteTools(locale).map((tool) => ({
		label: t(tool.tool.navigationKey),
		href: tool.pathname,
	}));

	const links = [
		{
			label: t("about"),
			href: "/about" as const,
		},
	];

	useEffect(() => {
		if (!isMenuOpen) return;

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;

			event.preventDefault();
			setIsMenuOpen(false);
			requestAnimationFrame(() => menuTriggerRef.current?.focus());
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [isMenuOpen]);

	return (
		<header className="sticky top-0 z-50 px-3 pt-3 sm:px-5 lg:px-8">
			<div className="bg-background/85 relative mx-auto flex h-14 w-full max-w-[90rem] items-center justify-between rounded-2xl border px-3 shadow-[0_10px_35px_-18px_rgba(15,23,42,0.35)] backdrop-blur-xl sm:px-4 lg:px-5 dark:shadow-[0_10px_35px_-18px_rgba(0,0,0,0.8)]">
				<div className="relative z-10">
					<Link href="/" className="flex items-center gap-2.5">
						<Image
							src={DEFAULT_LOGO_URL}
							alt={t("logoAlt")}
							width={30}
							height={30}
						/>
						<span className="text-lg font-bold tracking-tight">
							Editing.video
						</span>
					</Link>
				</div>

				<div className="relative z-10 flex items-center">
					<div className="flex items-center gap-3 md:hidden">
						<Button
							ref={menuTriggerRef}
							variant="text"
							size="icon"
							className="flex items-center justify-center p-0"
							aria-label={t("openMenu")}
							onClick={() => setIsMenuOpen(!isMenuOpen)}
						>
							<HugeiconsIcon icon={Menu02Icon} size={30} />
						</Button>
					</div>
					<div className="hidden items-center gap-1.5 md:flex">
						<nav className="mr-3 flex items-center gap-1 lg:mr-5 lg:gap-2">
							<NavigationMenu delayDuration={100} skipDelayDuration={200}>
								<NavigationMenuList>
									<NavigationMenuItem>
										<NavigationMenuTrigger
											className="h-9 rounded-lg bg-transparent px-2.5 text-sm font-medium hover:bg-accent/60 focus:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent/60 lg:px-3"
											onKeyDown={(event) => {
												if (
													event.key === "Enter" ||
													event.key === " " ||
													event.key === "ArrowDown"
												) {
													event.preventDefault();
													event.currentTarget.click();
												}
											}}
										>
											{t("tools")}
										</NavigationMenuTrigger>
										<NavigationMenuContent>
											<div className="w-56 p-2">
												{tools.map((tool) => (
													<NavigationMenuLink asChild key={tool.href}>
														<Link
															href={tool.href}
															className="flex min-h-11 cursor-pointer items-center rounded-lg px-3 py-2.5 text-sm font-semibold outline-hidden transition-colors duration-200 hover:bg-accent/70 focus-visible:bg-accent/70 focus-visible:ring-2 focus-visible:ring-ring"
														>
															{tool.label}
														</Link>
													</NavigationMenuLink>
												))}
											</div>
										</NavigationMenuContent>
									</NavigationMenuItem>
								</NavigationMenuList>
							</NavigationMenu>
							{links.map((link) => (
								<Button
									key={link.href}
									asChild
									variant="text"
									className="h-9 px-2.5 text-sm lg:px-3"
								>
									<Link href={link.href}>{link.label}</Link>
								</Button>
							))}
						</nav>
						<Button asChild className="h-9 rounded-xl px-4 text-sm">
							<Link
								href="/projects"
								onClick={() =>
									analyticsTrack({
										eventName: "editor_entry_clicked",
										properties: {
											surface: "landing_header",
											destination: "/projects",
										},
									})
								}
							>
								{t("getStarted")}
								<ArrowRight className="size-4" />
							</Link>
						</Button>
						<LanguageSelector />
						<ThemeToggle className="ml-0.5 size-9 rounded-xl" />
					</div>
				</div>
				<div
					className={cn(
						"bg-background/90 pointer-events-none fixed inset-0 opacity-0 backdrop-blur-3xl",
						"transition-opacity duration-150",
						isMenuOpen && "pointer-events-auto opacity-100",
					)}
				>
					<div className="relative h-full">
						<button
							type="button"
							aria-label={t("closeMenu")}
							className="absolute inset-0"
							onClick={closeMenu}
							onKeyDown={(event) => {
								if (
									event.key === "Enter" ||
									event.key === " " ||
									event.key === "Escape"
								) {
									event.preventDefault();
									closeMenu();
								}
							}}
						/>
						<nav className="flex flex-col gap-3 px-6 pt-[5rem]">
							<motion.div
								initial={{ scale: 0.98, opacity: 0 }}
								animate={{
									scale: isMenuOpen ? 1 : 0.98,
									opacity: isMenuOpen ? 1 : 0,
								}}
								transition={{
									duration: 0.4,
									ease: [0.25, 0.46, 0.45, 0.94],
								}}
							>
								<p className="text-muted-foreground mb-2 text-sm font-medium">
									{t("tools")}
								</p>
								{tools.map((tool) => (
									<Link
										key={tool.href}
										href={tool.href}
										className="bg-background/70 flex min-h-12 items-center rounded-2xl border px-4 py-3 text-base font-semibold shadow-sm backdrop-blur-xl transition-colors duration-200 hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
										onClick={closeMenu}
									>
										{tool.label}
									</Link>
								))}
							</motion.div>
							{links.map((link, index) => (
								<motion.div
									key={link.href}
									initial={{ scale: 0.98, opacity: 0 }}
									animate={{
										scale: isMenuOpen ? 1 : 0.98,
										opacity: isMenuOpen ? 1 : 0,
									}}
									transition={{
										duration: 0.4,
										delay: isMenuOpen ? (index + 1) * 0.1 : 0,
										ease: [0.25, 0.46, 0.45, 0.94],
									}}
								>
									<Link
										href={link.href}
										className="text-2xl font-semibold"
										onClick={() => setIsMenuOpen(false)}
									>
										{link.label}
									</Link>
								</motion.div>
							))}
						</nav>
						<LanguageSelector className="absolute bottom-8 left-6" />
						<ThemeToggle
							className="absolute right-8 bottom-8 size-10"
							iconClassName="!size-[1.2rem]"
							onToggle={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}}
						/>
					</div>
				</div>
			</div>
		</header>
	);
}
